import React, { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import type { CollectButtonSource } from '@soundxyz/utils/analytics';
import { WithReferralAddress } from '@soundxyz/utils/constants/embeds';
import { GetNotifiedButton } from '@components/EmailNotifications/GetNotifiedButton';
import { usePostsEnabled } from '@components/Feed/Post/usePostsEnabled';
import { formatCountdown } from '@components/Landing/sections/Hero/helpers';
import { LikeButton } from '@components/Likes/Button';
import { useLikeRelease } from '@components/Likes/useLikeRelease';
import { SLogoSoundBarOverlay } from '@components/SoundBar/SLogoSoundBarOverlay';
import { Waveform } from '@components/Waveform/Release/Waveform';
import { AddToShelfMenuItem } from '@components/basics/Dropdowns/MenuItems/AddToShelfMenuItem';
import { EditFeaturedSoundMenuItem } from '@components/basics/Dropdowns/MenuItems/EditFeaturedSoundMenuItem';
import { LikeSoundMenuItem } from '@components/basics/Dropdowns/MenuItems/LikeSoundMenuItem';
import { PostReleaseMenuItem } from '@components/basics/Dropdowns/MenuItems/PostReleaseMenuItem';
import { ShareMenuItem } from '@components/basics/Dropdowns/MenuItems/ShareMenuItem';
import { ThreeDotsDropdown } from '@components/basics/Dropdowns/ThreeDotsDropdown';
import { Button } from '@components/main-components/button/Button';
import { PlayIndicatorBars } from '@components/main-components/cards/PlayIndicatorBars';
import { NextFutureImage } from '@components/main-components/image/NextFutureImage';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { PlayButton } from '@components/main-components/play-button/PlayButton';
import { CollectModal } from '@components/modals/Collect/CollectModal';
import { useAuth } from '@context/auth/base';
import { useMediaQuery } from '@context/mediaQuery';
import { type MintReferral, MintReferralType } from '@context/mintReferral';
import { ModalType, useModal } from '@context/modal';
import type { FragmentType, SoundBarReleaseFragment } from '@graphql/reactQuery';
import { SoundBarReleaseFragmentDoc } from '@graphql/reactQuery';
import { getFragment, gql } from '@graphql/reactQuery';
import { useCheckReferrableRelease } from '@hooks/useCheckReferrable';
import { useGetDominantBackgroundColor } from '@hooks/useGetDominantBackgroundColor';
import { useHover } from '@hooks/useHover';
import { usePlayButton } from '@hooks/usePlayButton';
import { useTimer } from '@hooks/useTimer';
import { trackClickEvent } from '@lib/analytics';
import { DEFAULT_DOMINANT_BACKGROUND_COLOR } from '@utils/constants';
import { getStaticCoverImage } from '@utils/helpers';
import { webappUriPath } from '@utils/url';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment SoundBarRelease on Release {
    id
    title
    type
    artist {
      id
      user {
        id
      }
      name
      webappUri
    }
    totalMinted
    mintStartTimestamp
    staticCoverImage {
      id
      url
    }
    coverImage {
      id
      url
      dominantColor
    }
    track {
      id
      normalizedPeaks
      duration
    }
    goldenEggNft {
      id
      owner {
        id
      }
    }
    webappUri
    ...WaveformCommentsReleaseInfo
    ...CollectModal
    ...FeedPostModalMainSound
    ...ReleaseReferrableInfo
    ...LikeSoundMenuItem
  }
`);

enum SoundBarButtonAction {
  Collect = 'Collect',
  GetNotified = 'Get Notified',
}

function SoundBarButtonActionForRelease(release: SoundBarReleaseFragment) {
  // if the mint has started, collect otherwise get notified
  if (release.mintStartTimestamp <= Date.now()) {
    return SoundBarButtonAction.Collect;
  } else {
    return SoundBarButtonAction.GetNotified;
  }
}

export const SoundBar = ({
  releaseFrag,
  customSubTitle,
  collectButtonSource,
  mintReferral,
  showEditFeaturedSoundDropdownMenuItem,
}: {
  releaseFrag: FragmentType<SoundBarReleaseFragmentDoc>;
  customSubTitle?: React.ReactNode;
  collectButtonSource: CollectButtonSource;
  mintReferral: MintReferral;
  showEditFeaturedSoundDropdownMenuItem: boolean;
}) => {
  const { authUser } = useAuth();
  const { tablet } = useMediaQuery();
  const release = getFragment(SoundBarReleaseFragmentDoc, releaseFrag);

  const { openModal } = useModal();

  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  const soundBarButtonAction = useMemo(() => SoundBarButtonActionForRelease(release), [release]);
  const isAlbum = release.type === 'ALBUM';
  const {
    isPlaying,
    isLoading: isPlayButtonLoading,
    onPlayClick,
  } = usePlayButton({
    isAlbum: false,
    trackId: release.track.id,
  });

  const handleCollectButtonClick = useCallback(() => {
    trackClickEvent({
      event: 'Collect Now Clicked',

      properties: {
        associationId: release.id,
        associationType: 'Release',
        sourceComponent: collectButtonSource,
      },
    });

    openModal(ModalType.COLLECT, {
      body: (
        <CollectModal
          release={release}
          mintReferral={mintReferral}
          collectButtonSource={collectButtonSource}
        />
      ),
    });
  }, [openModal, release, mintReferral, collectButtonSource]);

  const dominantColor =
    release.coverImage.dominantColor === '#FFFFFF' || !release.coverImage.dominantColor
      ? DEFAULT_DOMINANT_BACKGROUND_COLOR
      : release.coverImage.dominantColor;

  const dominantBackgroundColor = useGetDominantBackgroundColor({ dominantColor });

  const userOwnsGoldenEgg =
    !!release.goldenEggNft && !!authUser && release.goldenEggNft.owner.id === authUser.id;

  const hasSaleStarted = release.mintStartTimestamp <= Date.now();
  const saleStartCountdown = useTimer({
    expiryTimestamp: release.mintStartTimestamp,
  });
  const formattedStartCountDown = formatCountdown({ ...saleStartCountdown, prefix: 'Starts in' });
  const [hovering, hoverProps] = useHover();

  const { viewingPostsEnabled } = usePostsEnabled();

  const { isShareReferrable } = useCheckReferrableRelease({
    release,
  });

  const { isLiked, onClick: onLikeClick } = useLikeRelease({ id: release.id, source: 'Card' });

  const songPagePath =
    mintReferral && mintReferral.referralType === MintReferralType.Post && isShareReferrable
      ? webappUriPath(
          WithReferralAddress(release.webappUri, {
            referralAddress: mintReferral.referralData.postUserAddress,
            source: {
              postId: mintReferral.referralData.postId,
            },
          }),
        )
      : webappUriPath(release.webappUri);

  return (
    <div className="flex flex-col gap-2" {...hoverProps}>
      <div
        className="align-center relative flex h-[100px] w-full items-center overflow-hidden rounded-lg px-4 md:h-[160px]"
        style={{ backgroundColor: dominantBackgroundColor }}
      >
        <div className="absolute inset-0 left-0 top-0 w-full overflow-hidden bg-neutral900 opacity-40" />
        <div className="absolute left-[300px] top-0 overflow-hidden">
          <SLogoSoundBarOverlay className="relative overflow-hidden" />
        </div>

        <div
          className={`color-white absolute right-2 top-2 z-above1 flex md:top-2 ${
            Boolean(hovering || isDropdownOpen) ? `md:flex` : `md:hidden`
          }`}
        >
          {!!authUser && (
            <div className="hidden h-fit w-fit md:block">
              <LikeButton
                variant="inverted"
                liked={isLiked}
                onClick={onLikeClick}
                disabled={false}
                iconSize={18}
              />
            </div>
          )}

          <ThreeDotsDropdown
            isDropdownOpen={isDropdownOpen}
            setIsDropdownOpen={setIsDropdownOpen}
            variant="inverted"
          >
            {authUser && showEditFeaturedSoundDropdownMenuItem && (
              <EditFeaturedSoundMenuItem
                label="Edit Featured Sound"
                webappUri={authUser.webappUri}
              />
            )}

            {viewingPostsEnabled && (
              <PostReleaseMenuItem releaseFrag={release} source="Sound Bar" />
            )}

            {!isAlbum && (
              <div className="w-full md:hidden">
                <LikeSoundMenuItem liked={isLiked} onClick={onLikeClick} data={release} />
              </div>
            )}

            <AddToShelfMenuItem releaseId={release.id} source="Collector Profile" />
            <ShareMenuItem releaseId={release.id} source="Collector Profile" />
          </ThreeDotsDropdown>
        </div>

        <div className="relative flex flex-1 items-center gap-2 overflow-hidden  md:items-stretch">
          <div className=" relative aspect-square w-[68px] flex-00auto overflow-hidden rounded-md transition-opacity duration-300 ease-in-out md:w-[127px] md:rounded-lg">
            <NextFutureImage
              alt="featured release image"
              src={getStaticCoverImage({
                coverImage: release.coverImage.url,
                staticCoverImage: release.staticCoverImage?.url,
              })}
              fill
              sizes="127px"
            />
            <div
              className={clsx(
                'absolute inset-0',
                'items-center justify-center transition-opacity duration-300 ease-in-out md:opacity-0 md:hover:opacity-100',
                'z-above1 hidden md:flex',
              )}
            >
              <PlayButton
                color="white"
                playing={isPlaying}
                onClick={onPlayClick}
                size="s"
                loading={isPlayButtonLoading}
              />
            </div>
          </div>

          <div className="flex flex-1 flex-col justify-between overflow-hidden">
            <div className="flex flex-col gap-1 md:pt-1">
              <div className="overflow-hidden text-ellipsis whitespace-nowrap font-base text-base-s text-white">
                <AccessibleLink pathnameUri={webappUriPath(release.artist.webappUri)}>
                  {release.artist.name}
                </AccessibleLink>
              </div>
              <div className="flex items-center gap-2 whitespace-nowrap font-title text-title-s font-medium text-white">
                {isPlaying && <PlayIndicatorBars smallBars={!tablet} />}
                <AccessibleLink
                  className="overflow-hidden text-ellipsis"
                  pathnameUri={songPagePath}
                >
                  {release.title}
                </AccessibleLink>
              </div>
              <div className="flex items-center gap-1">
                {userOwnsGoldenEgg && (
                  <NextFutureImage
                    alt="goldenEggBadge"
                    src="/images/golden-egg-badge.png"
                    className="h-4 w-4"
                    width={16}
                    height={16}
                  />
                )}
                {customSubTitle || (
                  <div className="flex gap-2 font-base text-base-s text-white opacity-60">
                    {!hasSaleStarted && <div className="">{formattedStartCountDown}</div>}
                    {!hasSaleStarted && <div className="">•</div>}
                    <div>{`${release.totalMinted} minted`}</div>
                  </div>
                )}
              </div>
            </div>

            <div className="hidden items-center gap-2 md:flex">
              <Waveform
                track={release.track}
                releaseFrag={release}
                isTrackDisabled={false}
                showComments={false}
                showBottomHalf={false}
                enableTouchHandler
              />
              {soundBarButtonAction === SoundBarButtonAction.GetNotified ? (
                <GetNotifiedButton
                  type="sound_bar"
                  artistId={release.artist.id}
                  userId={release.artist.user.id}
                />
              ) : (
                <Button
                  variant="secondary"
                  className="self-end border-0"
                  label="Collect"
                  size="S"
                  onClick={handleCollectButtonClick}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
